import { canSubmitPersonalTimesheet } from './personal-timesheets/personalTimesheetAccess';
import React, { Suspense, memo, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { NotificationItem, User } from '../types';
import api from '../services/api';
import { usePageTitle } from '../context/PageTitleContext';
import { usePageLayout } from '../context/PageLayoutContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { hasModuleAccess, hasSubPermission } from '../utils/modulePermissions';
import { MaintenanceBanner } from './MaintenanceBanner';
import { ProjectSidebarContent } from './ProjectSidebarContent';
import { useProjectWorkspaceContext } from '../context/ProjectWorkspaceContext';
import { APP_RELEASE_LABEL } from '../config/appMeta';
import { LoadingSpinner } from './LoadingStates';
import { Announcements } from './Announcements';
import { isPermissionPreview } from '../services/permissionPreviewSession';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface LayoutProps {
  user: User | null;
  onLogout: () => void;
  children: React.ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  key: string;
  permissionKey?: string;
  adminOnly?: boolean;
  ownerOnly?: boolean;
  developerOnly?: boolean;
}

// ─── Navigation structure ──────────────────────────────────────────────────────

const NAV_GROUPS: { labelKey: string; items: NavItem[] }[] = [
  {
    labelKey: 'nav.general',
    items: [
      { path: '/dashboard', label: 'Dashboard', key: 'dashboard' },
      { path: '/home', label: 'Home', key: 'personal_home' },
      { path: '/my-hours', label: 'My hours', key: 'my_hours' },
      { path: '/projects', label: 'Projects', key: 'projects' },
      { path: '/estimator', label: 'Estimator workspace', key: 'estimates_home', permissionKey: 'estimates' },
      { path: '/bids', label: 'Bids / Estimates', key: 'estimates' },
    ],
  },
  {
    labelKey: 'nav.operations',
    items: [
      { path: '/timesheets',    label: 'Timesheets', key: 'timesheets'    },
      { path: '/reports', label: 'Reports', key: 'reports' },
      { path: '/requests', label: 'Requests', key: 'requests' },
      { path: '/employee-hours', label: 'Emp. Hours', key: 'employee_hours' },
    ],
  },
  {
    labelKey: 'nav.resources',
    items: [
      { path: '/people', label: 'People', key: 'people' },
      // Foremen: the people on their own projects (not the admin People module).
      { path: '/equipo', label: 'People', key: 'my_people' },
      { path: '/warehouse', label: 'Warehouse', key: 'warehouse' },
    ],
  },
  {
    labelKey: 'nav.analysis',
    items: [
      // Schedule lives inside each project. Reports also has a global daily directory.
      { path: '/export', label: 'Exports', key: 'export' },
    ],
  },
  {
    labelKey: 'nav.administration',
    items: [
      { path: '/security-center', label: 'Security', key: 'security'  },
      { path: '/activity-log', label: 'Activity Log', key: 'activity_log', permissionKey: 'security', ownerOnly: true },
      { path: '/settings',        label: 'Settings', key: 'settings'  },
    ],
  },
  {
    labelKey: 'nav.inProgressGroup',
    items: [
      { path: '/in-progress', label: 'In Progress', key: 'in_progress', developerOnly: true },
    ],
  },
];

// Easy mode for foremen: Tasks comes first, then the normal menu for whatever
// modules their permissions allow, without the general dashboard.
const GUIDED_HIDDEN_NAV_KEYS = new Set(['dashboard']);
function guidedNavGroups(language: string): { labelKey: string; items: NavItem[] }[] {
  const es = language === 'es';
  return [{
    labelKey: 'nav.guided',
    items: [
      { path: '/inicio', label: es ? 'Tareas' : 'Tasks', key: 'guided_home' },
    ],
  }, ...NAV_GROUPS.map((group) => ({ ...group, items: group.items.filter((item) => !GUIDED_HIDDEN_NAV_KEYS.has(item.key)) }))];
}

// ─── Icons ─────────────────────────────────────────────────────────────────────

const NAV_ICONS: Record<string, React.ReactNode> = {
  personal_home: <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="m3 10 9-7 9 7v10a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1V10Z" /></svg>,
  dashboard: (
    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  ),
  timesheets: (
    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2m-6 9l2 2 4-4" />
    </svg>
  ),
  projects: (
    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
    </svg>
  ),
  schedule: (
    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3M4 11h16M6 5h12a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 15h3m2 0h3" />
    </svg>
  ),
  guided_home: (
    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7 3h10a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
    </svg>
  ),
  reports: (
    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6M9 16h4" />
    </svg>
  ),
  employee_hours: (
    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2zm0 0V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10m-6 0a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2m0 0V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2z" />
    </svg>
  ),
  field_logs: (
    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 12v4m0 0l-2-2m2 2l2-2" />
    </svg>
  ),
  control: (
    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2zm0 0V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10m-6 0a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2m0 0V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2z" />
    </svg>
  ),
  my_people: (
    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm10 10v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  people: (
    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm10 10v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  export: (
    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3M4 17v1a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-1" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v4H6V6z" />
    </svg>
  ),
  settings: (
    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 0 0-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 0 0-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 0 0-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 0 0-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 0 0 1.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
    </svg>
  ),
  security: (
    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l7 4v5c0 5-3.5 8.5-7 9-3.5-.5-7-4-7-9V7l7-4z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.5 12.5l1.75 1.75L14.75 10.5" />
    </svg>
  ),
  activity_log: (
    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5a3 3 0 0 1 6 0v1H9V5zm0 7h6m-6 4h4" />
    </svg>
  ),
  in_progress: (
    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 19h16" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 16l4-5 3 3 5-8" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 6h3v3" />
    </svg>
  ),
  requests: <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}><path d="M9 5H5v16h14V5h-4M9 3h6v4H9zM8 12h8M8 16h8" /></svg>,
  warehouse: (
    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 9.5L12 4l9 5.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 21V12h6v9" />
    </svg>
  ),
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

function avatarColor(str: string) {
  const palette = ['bg-sky-500','bg-violet-500','bg-teal-500','bg-purple-500','bg-pink-500','bg-cyan-600','bg-rose-500','bg-emerald-500'];
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) & 0xffffffff;
  return palette[Math.abs(h) % palette.length];
}

function userInitials(email: string, name?: string) {
  if (name) return name.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();
  return email.split('@')[0].slice(0, 2).toUpperCase();
}

function fmtNotifTime(value: string, t: (key: string, params?: Record<string, string | number>) => string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return t('layout.justNow');
  if (mins < 60) return t('layout.minutesAgo', { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return t('layout.hoursAgo', { count: hrs });
  const days = Math.floor(hrs / 24);
  if (days < 7)  return t('layout.daysAgo', { count: days });
  return date.toLocaleDateString();
}

function isNotificationRead(value: unknown) {
  return value === true || value === 1 || value === '1';
}

/**
 * Badge count. The list endpoint returns only the latest 30 rows, but also
 * `unread_count` across all of the user's notifications. Use the visible rows
 * (which include optimistic/local read state) plus the server's unread rows
 * that fall outside the visible page.
 */
function computeUnreadCount(
  rawRows: NotificationItem[],
  displayedRows: NotificationItem[],
  serverUnreadCount: unknown,
  allReadBefore: number | null,
) {
  const visibleUnread = displayedRows.filter(n => !isNotificationRead(n.is_read)).length;
  const serverUnread = Number(serverUnreadCount);
  if (!Number.isFinite(serverUnread)) return visibleUnread;
  const serverUnreadInPage = rawRows.filter(n => !isNotificationRead(n.is_read)).length;
  let hiddenUnread = Math.max(0, serverUnread - serverUnreadInPage);
  if (hiddenUnread > 0 && allReadBefore !== null && rawRows.length > 0) {
    // Rows beyond the page are older than the oldest visible row; if that row
    // is already covered by a local "mark all read", so are they.
    const oldestVisibleAt = new Date(rawRows[rawRows.length - 1].created_at).getTime();
    if (!Number.isFinite(oldestVisibleAt) || oldestVisibleAt <= allReadBefore) hiddenUnread = 0;
  }
  return visibleUnread + hiddenUnread;
}

function SidebarToggleIcon({ collapsed }: { collapsed: boolean }) {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.9}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 5h3M4 12h3M4 19h3" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 5v14" />
      <path strokeLinecap="round" strokeLinejoin="round" d={collapsed ? 'M14 6l5 6-5 6' : 'M19 6l-5 6 5 6'} />
    </svg>
  );
}

// ─── Sidebar nav item ─────────────────────────────────────────────────────────
// Module scope (not nested in Layout) so React keeps the same component type
// across Layout renders and nav links are not unmounted/remounted each time.

function navTranslationKey(item: NavItem) {
  return item.key === 'employee_hours'
    ? 'nav.employeeHours'
    : item.key === 'export'
    ? 'nav.exports'
    : item.key === 'in_progress'
    ? 'nav.in_progress'
    : `nav.${item.key}`;
}

const SideItem = memo(function SideItem({
  item,
  active,
  collapsed,
  label,
}: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  label: string;
}) {
  return (
    <Link
      to={item.path}
      title={collapsed ? label : undefined}
      className={[
        'group flex items-center gap-3 rounded-lg text-sm font-medium transition-[background-color,color,box-shadow,transform] duration-[var(--motion-state)] outline-none motion-reduce:transition-none',
        collapsed ? 'justify-center p-2.5' : 'px-3 py-2.5',
        active
          ? 'bg-white/10 text-white'
          : 'text-slate-400 hover:bg-white/[0.06] hover:text-slate-100',
      ].join(' ')}
      style={active && !collapsed ? { boxShadow: 'inset 3px 0 0 #F97316' } : {}}
    >
      <span className={`flex-shrink-0 transition-colors ${active ? 'text-orange-400' : 'text-slate-500 group-hover:text-slate-300'}`}>
        {NAV_ICONS[item.key === 'my_hours' ? 'timesheets' : item.key]}
      </span>
      {!collapsed && <span className="truncate flex-1">{label}</span>}
      {!collapsed && active && (
        <span className="w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0" />
      )}
    </Link>
  );
});

// ─── Component ─────────────────────────────────────────────────────────────────

export function Layout({ user, onLogout, children }: LayoutProps) {
  const [announcementHistoryOpen, setAnnouncementHistoryOpen] = useState(false);
  const navigate  = useNavigate();
  const location  = useLocation();
  const { pageTitle: contextTitle } = usePageTitle();
  const { disabledModules: projectDisabledModules } = useProjectWorkspaceContext();

  // Sidebar
  const [sidebarOpen,      setSidebarOpen]      = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('sidebarCollapsed') === 'true',
  );
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  );
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );

  // Notifications
  const [notifications,     setNotifications]    = useState<NotificationItem[]>([]);
  const [unreadCount,       setUnreadCount]      = useState(0);
  const [notifOpen,         setNotifOpen]        = useState(false);
  const [accountMenuOpen,   setAccountMenuOpen]  = useState(false);

  // Branding
  const [companyLogo, setCompanyLogo] = useState('');
  const [companyName, setCompanyName] = useState('');

  const sidebarRef = useRef<HTMLElement>(null);
  const notifRef   = useRef<HTMLDivElement>(null);
  const accountRef = useRef<HTMLDivElement>(null);
  const localReadIdsRef = useRef<Set<number>>(new Set());
  const localAllReadBeforeRef = useRef<number | null>(null);
  const notificationRequestRef = useRef<Promise<void> | null>(null);
  const lastNotificationFetchAtRef = useRef(0);

  const { noPadding } = usePageLayout();
  const { isDark, toggleTheme } = useTheme();
  const { t, language } = useLanguage();
  const isGuided = Boolean(user?.guided_settings?.enabled);

  const isDeveloper = Number(user?.is_developer || 0) === 1;
  const isOperator  = user?.role === 'admin' && Number(user?.is_operator || user?.is_owner_admin || 0) === 1;
  const isOwner     = isOperator; // alias kept for nav checks below
  const roleLabel   = isDeveloper
    ? t('account.role.developer')
    : user?.role === 'admin'
    ? (isOperator ? t('account.role.operator') : t('account.role.admin'))
    : t('account.role.supervisor');

  const initials    = userInitials(user?.email || '', user?.display_name || user?.name);
  const avatarCls   = avatarColor(user?.email || '');
  const displayName = user?.display_name || user?.name || user?.email?.split('@')[0] || '';
  const projectRouteMatch = location.pathname.match(/^\/projects\/(\d+)(?:\/|$)/);
  const projectRouteId = projectRouteMatch?.[1] ?? null;
  const showProjectSidebar = projectRouteId !== null && isDesktop;
  const isDrawingViewerRoute = /^\/projects\/\d+\/(?:drawings\/\d+\/view|drawing-sets\/\d+\/pages\/\d+\/view)\/?$/.test(location.pathname);
  const crossfadeDuration = prefersReducedMotion ? '0ms' : '150ms';
  const label = (() => {
    if (location.pathname.startsWith('/projects/')) return t('nav.projects');
    const map: Record<string, string> = {
      '/dashboard': 'nav.dashboard',
      '/timesheets': 'nav.timesheets',
      '/projects': 'nav.projects',
      '/schedule': 'nav.schedule',
      '/account': 'nav.account',
      '/people': 'nav.people',
      '/reports': 'nav.reports',
      '/employee-hours': 'nav.employeeHours',
      '/export': 'nav.exports',
      '/security-center': 'nav.security',
      '/activity-log': 'nav.activityLog',
      '/settings': 'nav.settings',
      '/warehouse': 'nav.warehouse',
      '/requests': 'nav.requests',
      '/in-progress': 'nav.in_progress',
    };
    return t(map[location.pathname] || '');
  })();

  // Persist collapse
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  useEffect(() => {
    const desktopQuery = window.matchMedia('(min-width: 1024px)');
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleDesktopChange = (event: MediaQueryListEvent) => setIsDesktop(event.matches);
    const handleMotionChange = (event: MediaQueryListEvent) => setPrefersReducedMotion(event.matches);
    desktopQuery.addEventListener('change', handleDesktopChange);
    motionQuery.addEventListener('change', handleMotionChange);
    return () => {
      desktopQuery.removeEventListener('change', handleDesktopChange);
      motionQuery.removeEventListener('change', handleMotionChange);
    };
  }, []);

  useEffect(() => {
    setSidebarOpen(false);
    setNotifOpen(false);
    setAccountMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    localReadIdsRef.current = new Set();
    localAllReadBeforeRef.current = null;
    if (!user?.id) return;

    try {
      const raw = localStorage.getItem(`forxa.notificationReadState.${user.id}`);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { ids?: number[]; allReadBefore?: number | null };
      localReadIdsRef.current = new Set((parsed.ids || []).map(Number).filter(Number.isFinite));
      localAllReadBeforeRef.current = Number.isFinite(Number(parsed.allReadBefore))
        ? Number(parsed.allReadBefore)
        : null;
    } catch {
      localReadIdsRef.current = new Set();
      localAllReadBeforeRef.current = null;
    }
  }, [user?.id]);

  const persistLocalReadState = useCallback(() => {
    if (!user?.id) return;
    try {
      const ids = Array.from(localReadIdsRef.current).slice(-300);
      localStorage.setItem(`forxa.notificationReadState.${user.id}`, JSON.stringify({
        ids,
        allReadBefore: localAllReadBeforeRef.current,
      }));
    } catch { /* localStorage can be unavailable in strict browser modes */ }
  }, [user?.id]);

  const applyLocalReadState = useCallback((rows: NotificationItem[]) => {
    const allReadBefore = localAllReadBeforeRef.current;
    return rows.map((notification) => {
      const id = Number(notification.id);
      const createdAt = new Date(notification.created_at).getTime();
      const locallyRead = localReadIdsRef.current.has(id)
        || (allReadBefore !== null && (!Number.isFinite(createdAt) || createdAt <= allReadBefore));
      const read = locallyRead || isNotificationRead(notification.is_read);
      return { ...notification, is_read: read ? 1 : 0 };
    });
  }, []);

  const loadNotifs = useCallback(async () => {
    if (notificationRequestRef.current) {
      await notificationRequestRef.current;
      return;
    }
    lastNotificationFetchAtRef.current = Date.now();
    const request = (async () => {
      try {
        const d = await api.getNotifications();
        const rawNotifications: NotificationItem[] = d.notifications || [];
        const nextNotifications = applyLocalReadState(rawNotifications);
        setNotifications(nextNotifications);
        setUnreadCount(computeUnreadCount(rawNotifications, nextNotifications, d.unread_count, localAllReadBeforeRef.current));
      } catch { /* resilient */ }
    })();
    notificationRequestRef.current = request;
    try {
      await request;
    } finally {
      if (notificationRequestRef.current === request) notificationRequestRef.current = null;
    }
  }, [applyLocalReadState]);

  useEffect(() => {
    if (!user) return;
    const syncBranding = () => {
      api.getBranding().then((s: any) => {
        setCompanyLogo(s.company_logo || '');
        setCompanyName(s.company_name || '');
      }).catch(() => {
        setCompanyLogo('');
        setCompanyName('');
      });
    };

    syncBranding();
    const onBrandingUpdated = () => syncBranding();
    window.addEventListener('company-branding-updated', onBrandingUpdated as EventListener);
    return () => {
      window.removeEventListener('company-branding-updated', onBrandingUpdated as EventListener);
    };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    loadNotifs();
    // Runs on every page for every logged-in user — skip while the tab is
    // hidden so background/minimized tabs stop holding DB connections every
    // cycle. Also relaxed from 30s to 60s; focus/visibilitychange below still
    // refreshes immediately when the user actually comes back.
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      loadNotifs();
    }, 60_000);
    let focusRefreshTimer: number | null = null;
    const requestVisibleRefresh = () => {
      if (document.visibilityState === 'hidden') return;
      if (Date.now() - lastNotificationFetchAtRef.current < 15_000) return;
      if (focusRefreshTimer !== null) window.clearTimeout(focusRefreshTimer);
      focusRefreshTimer = window.setTimeout(() => {
        focusRefreshTimer = null;
        loadNotifs();
      }, 150);
    };
    const onFocus = () => requestVisibleRefresh();
    const onVis = () => requestVisibleRefresh();
    const onOut   = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node))
        setNotifOpen(false);
      if (accountRef.current && !accountRef.current.contains(e.target as Node))
        setAccountMenuOpen(false);
      if (sidebarRef.current && !sidebarRef.current.contains(e.target as Node)
          && !(e.target as HTMLElement).closest('[data-sidebar-toggle]'))
        setSidebarOpen(false);
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    document.addEventListener('mousedown', onOut);
    return () => {
      window.clearInterval(interval);
      if (focusRefreshTimer !== null) window.clearTimeout(focusRefreshTimer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      document.removeEventListener('mousedown', onOut);
    };
  }, [user, loadNotifs]);

  function handleNotifClick(n: NotificationItem) {
    // Close dropdown and navigate immediately (don't wait for API)
    setNotifOpen(false);
    if (n.link) navigate(n.link);

    if (!n.is_read) {
      localReadIdsRef.current.add(Number(n.id));
      persistLocalReadState();
      // Optimistic update — mark read in local state right away
      setNotifications(cur => cur.map(x => x.id === n.id ? { ...x, is_read: 1 } : x));
      setUnreadCount(c => Math.max(0, c - 1));
      // Fire-and-forget: reconcile immediately if the API fails.
      api.markNotificationRead(n.id).then(() => loadNotifs()).catch(() => loadNotifs());
    }
  }

  function handleMarkAll() {
    localAllReadBeforeRef.current = Date.now() + 1000;
    notifications.forEach((notification) => localReadIdsRef.current.add(Number(notification.id)));
    persistLocalReadState();
    // Optimistic update — respond instantly, let the poll reconcile if API fails
    setNotifications(cur => cur.map(x => ({ ...x, is_read: 1 })));
    setUnreadCount(0);

    // Fire-and-forget — reconcile immediately if the API fails.
    api.markAllNotificationsRead().then(() => loadNotifs()).catch(() => loadNotifs());
  }

  function isActive(path: string) {
    // Guided "My tasks" and "My reports" share /inicio and differ by ?view=.
    if (path.startsWith('/inicio')) {
      const view = new URLSearchParams(location.search).get('view');
      return location.pathname === '/inicio' && (path.includes('view=reports') ? view === 'reports' : view !== 'reports');
    }
    return location.pathname === path ||
      (path !== '/dashboard' && location.pathname.startsWith(path));
  }

  function canSee(item: NavItem) {
    if (item.key === 'my_hours') return canSubmitPersonalTimesheet(user);
    if(item.key==='personal_home')return !hasModuleAccess(user,'dashboard','view') && hasModuleAccess(user,'requests','view')
      && ['warehouse.requests_prepare','warehouse.requests_dispatch','warehouse.requests_record_delivery'].some(key=>hasSubPermission(user,key as any));
    if (item.key === 'guided_home') return hasSubPermission(user, 'projects.tasks.view');
    if (item.key === 'my_people') return user?.role !== 'admin' && hasSubPermission(user, 'projects.workforce.people');
    if (item.developerOnly && !isDeveloper) return false;
    if (item.ownerOnly && !isOwner && !isDeveloper) return false;
    if (item.adminOnly && user?.role !== 'admin') return false;
    if (item.key === 'reports') {
      return hasModuleAccess(user, 'reports' as any, 'view')
        || hasModuleAccess(user, 'field_logs' as any, 'view')
        || hasModuleAccess(user, 'free_reports' as any, 'view')
        || hasModuleAccess(user, 'control' as any, 'view');
    }
    if (item.key === 'security') {
      if (isDeveloper || isOwner) return true;
      return hasModuleAccess(user, 'security' as any, 'view');
    }
    return hasModuleAccess(user, (item.permissionKey || item.key) as any, 'view');
  }

  // Lazy route chunks suspend here, inside the shell, so the first visit to a
  // page shows an inline spinner instead of replacing the whole app with the
  // full-screen splash.
  const routedContent = (
    <Suspense
      fallback={(
        <div className="flex flex-1 items-center justify-center py-16">
          <LoadingSpinner size={20} label={t('common.loading')} />
        </div>
      )}
    >
      {children}
    </Suspense>
  );

  // ── Sidebar width ─────────────────────────────────────────────────────────────
  const sw = sidebarCollapsed ? 'lg:w-[72px]' : 'lg:w-[232px]';

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50" style={isDark ? { background: '#07131C' } : undefined}>

      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ══════════════════ SIDEBAR ══════════════════ */}
      <aside
        ref={sidebarRef}
        style={{
          background: '#0D1F2D',
          borderRight: isDark ? '1px solid #1B3142' : 'none',
        }}
        className={[
          'fixed top-0 left-0 h-full z-50 flex flex-col overflow-hidden',
          'transform transition-[transform,width] duration-[var(--motion-overlay)] ease-[var(--motion-ease-standard)] motion-reduce:transition-none',
          sidebarOpen ? 'translate-x-0 w-[85vw] max-w-[320px]' : '-translate-x-full w-[85vw] max-w-[320px]',
          `lg:relative lg:translate-x-0 lg:z-auto lg:flex-shrink-0 ${sw}`,
        ].join(' ')}
      >

        {/* Brand */}
        <div className="border-b border-slate-800 flex-shrink-0">
          <div className={[
            'flex items-center py-[14px]',
            sidebarCollapsed ? 'justify-end px-4' : 'justify-between px-4',
          ].join(' ')}>
            {/* Logo + name */}
            {!sidebarCollapsed && (
              <div className="flex items-center gap-3 min-w-0">
                {companyLogo ? (
                  <img src={companyLogo} alt="logo" className="h-8 w-8 object-contain rounded-lg border border-white/10 bg-white/5 flex-shrink-0" />
                ) : (
                  <div className="h-8 w-8 flex-shrink-0">
                    <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
                      <g transform="translate(100 100) rotate(45)">
                        <rect x="-86" y="-22" width="172" height="44" rx="4" fill="#F97316"/>
                        <circle cx="-66" cy="-10" r="3" fill="#0D1F2D" opacity="0.55"/>
                        <circle cx="-66" cy="10" r="3" fill="#0D1F2D" opacity="0.55"/>
                        <circle cx="66" cy="-10" r="3" fill="#0D1F2D" opacity="0.55"/>
                        <circle cx="66" cy="10" r="3" fill="#0D1F2D" opacity="0.55"/>
                      </g>
                      <g transform="translate(100 100) rotate(-45)">
                        <rect x="-86" y="-22" width="172" height="44" rx="4" fill="#F97316"/>
                        <circle cx="-66" cy="-10" r="3" fill="#0D1F2D" opacity="0.55"/>
                        <circle cx="-66" cy="10" r="3" fill="#0D1F2D" opacity="0.55"/>
                        <circle cx="66" cy="-10" r="3" fill="#0D1F2D" opacity="0.55"/>
                        <circle cx="66" cy="10" r="3" fill="#0D1F2D" opacity="0.55"/>
                      </g>
                      <circle cx="100" cy="100" r="6" fill="#0D1F2D"/>
                      <circle cx="100" cy="100" r="2.5" fill="#F97316"/>
                    </svg>
                  </div>
                )}
                <div className="min-w-0">
                  {companyName ? (
                    <p className="text-[13px] font-semibold text-white truncate leading-tight">{companyName}</p>
                  ) : (
                    <p className="text-[14px] font-bold text-white tracking-[0.14em] leading-tight">
                      FOR<span style={{ color: '#F97316' }}>X</span>A
                    </p>
                  )}
                  <div className="mt-0.5 space-y-0.5">
                    <p className="text-[10px] leading-tight" style={{ color: 'rgba(255,255,255,0.3)' }}>
                      {companyName ? t('layout.poweredByForxa') : t('layout.builtForField')}
                    </p>
                    {APP_RELEASE_LABEL && (
                      <p
                        className="text-[10px] leading-tight tracking-[0.12em] uppercase"
                        style={{ color: 'rgba(249,115,22,0.58)' }}
                      >
                        {APP_RELEASE_LABEL}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Controls */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                className="hidden lg:flex items-center justify-center w-8 h-8 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-slate-800 transition-colors"
                onClick={() => setSidebarCollapsed((current) => !current)}
                title={sidebarCollapsed ? t('layout.expandSidebar') : t('layout.collapseSidebar')}
              >
                <SidebarToggleIcon collapsed={sidebarCollapsed} />
              </button>
              {/* Mobile close */}
              <button
                className="lg:hidden w-6 h-6 flex items-center justify-center rounded-md text-slate-500 hover:text-slate-300"
                onClick={() => setSidebarOpen(false)}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Nav — crossfade between global and project content in one sidebar slot */}
        <div className="relative flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-slate-700">
          <div
            aria-hidden={showProjectSidebar}
            style={{
              position: showProjectSidebar ? 'absolute' : 'relative',
              inset: 0,
              opacity: showProjectSidebar ? 0 : 1,
              visibility: showProjectSidebar ? 'hidden' : 'visible',
              pointerEvents: showProjectSidebar ? 'none' : 'auto',
              transition: `opacity ${crossfadeDuration} ease-out, visibility 0s linear ${showProjectSidebar ? crossfadeDuration : '0ms'}`,
            }}
          >
            <div className={`space-y-5 py-3 ${sidebarCollapsed ? 'px-1' : 'px-3'}`}>
              {(isGuided ? guidedNavGroups(language) : NAV_GROUPS).map(group => {
                const visible = group.items.filter(canSee);
                if (visible.length === 0) return null;
                return (
                  <div key={group.labelKey} className="space-y-0.5">
                    {!sidebarCollapsed ? (
                      <p className="px-3 pb-1 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                        {t(group.labelKey)}
                      </p>
                    ) : (
                      <div className="border-t border-slate-800 mx-1 mb-1 mt-0" />
                    )}
                    {visible.map(item => (
                      <SideItem
                        key={item.path}
                        item={item}
                        active={isActive(item.path)}
                        collapsed={sidebarCollapsed}
                        label={item.key==='my_hours' ? (language==='es'?'Mis horas':'My hours') : item.key==='estimates' ? (language==='es'?'Licitaciones / Estimados':'Bids / Estimates') : item.key==='estimates_home' ? (language==='es'?'Workspace del estimador':'Estimator workspace') : item.key==='personal_home' ? (language==='es'?'Inicio':'Home') : t(navTranslationKey(item), undefined, item.label)}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          <div
            aria-hidden={!showProjectSidebar}
            style={{
              position: showProjectSidebar ? 'relative' : 'absolute',
              inset: 0,
              opacity: showProjectSidebar ? 1 : 0,
              visibility: showProjectSidebar ? 'visible' : 'hidden',
              pointerEvents: showProjectSidebar ? 'auto' : 'none',
              transition: `opacity ${crossfadeDuration} ease-out, visibility 0s linear ${showProjectSidebar ? '0ms' : crossfadeDuration}`,
            }}
          >
            {projectRouteId && (
              <ProjectSidebarContent
                projectId={projectRouteId}
                projectName={contextTitle}
                user={user}
                collapsed={sidebarCollapsed}
                disabledModules={projectDisabledModules}
              />
            )}
          </div>
        </div>

        {/* User block */}
        <div className="border-t border-slate-800 flex-shrink-0 p-3">
          {sidebarCollapsed ? (
            <div className="flex items-center justify-center">
              <button
                title={`${displayName} - ${t('layout.signOut')}`}
                onClick={() => { onLogout(); navigate('/login'); }}
                className="group relative"
              >
                {user?.profile_photo ? (
                  <img src={user.profile_photo} alt="Profile" className="w-8 h-8 rounded-full object-cover border border-white/10" />
                ) : (
                  <div className={`w-8 h-8 rounded-full ${avatarCls} flex items-center justify-center text-white text-[11px] font-bold`}>
                    {initials}
                  </div>
                )}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl ring-1" style={{ background: 'rgba(255,255,255,0.05)', boxShadow: '0 0 0 1px rgba(255,255,255,0.07)' }}>
              {user?.profile_photo ? (
                <img src={user.profile_photo} alt="Profile" className="w-8 h-8 rounded-full object-cover border border-white/10 flex-shrink-0" />
              ) : (
                <div className={`w-8 h-8 rounded-full ${avatarCls} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                  {initials}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-slate-100 truncate leading-tight">{displayName}</p>
                <p className="text-[11px] text-slate-500 capitalize leading-tight">{roleLabel}</p>
              </div>
              <button
                onClick={() => { onLogout(); navigate('/login'); }}
                title={t('layout.signOut')}
                className="flex-shrink-0 p-1.5 rounded-lg text-slate-600 hover:text-red-400 hover:bg-slate-700/80 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3v1" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* ══════════════════ MAIN ══════════════════ */}
      <main className="forxa-app-shell flex-1 min-w-0 flex flex-col overflow-hidden">

        {/* Maintenance banner */}
        <div className={isDrawingViewerRoute ? 'hidden lg:contents' : 'contents'}>
          <MaintenanceBanner />
        </div>

        {/* Top bar */}
        <header
          className={`${isDrawingViewerRoute ? 'hidden lg:block' : 'block'} border-b flex-shrink-0 sticky top-0 z-40 ${isDark ? '' : 'bg-white border-slate-200'}`}
          style={isDark ? { background: '#0D1F2D', borderColor: '#1B3142' } : undefined}
        >
          <div className="px-4 sm:px-6 h-12 flex items-center gap-3">

            {/* Mobile hamburger */}
            <button
              data-sidebar-toggle="true"
              className="lg:hidden p-1.5 -ml-1 rounded-lg text-slate-400 transition-colors"
              style={{ ':hover': { background: '#122538' } } as any}
              onClick={() => setSidebarOpen(o => !o)}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            {/* Breadcrumb title */}
            <div className="flex-1 min-w-0 flex items-center gap-2">
              {contextTitle ? (
                <>
                  <span className="text-[13px] text-slate-500 truncate hidden sm:block">{label}</span>
                  <span className="text-slate-600 hidden sm:block text-xs">/</span>
                  <h2 className={`text-[13px] font-semibold truncate ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{contextTitle}</h2>
                </>
              ) : (
                <h2 className={`text-[13px] font-semibold truncate ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{label}</h2>
              )}
            </div>

            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              title={isDark ? t('layout.switchToLight') : t('layout.switchToDark')}
              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${isDark ? 'text-slate-400 hover:bg-white/10' : 'text-slate-500 hover:bg-slate-100'}`}
            >
              {isDark ? (
                /* Sun icon */
                <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
                  <circle cx="12" cy="12" r="5" />
                  <path strokeLinecap="round" d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                </svg>
              ) : (
                /* Moon icon */
                <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              )}
            </button>

            {/* Notifications */}
            <div className="relative flex-shrink-0" ref={notifRef}>
              <button
                onClick={() => { if (!notifOpen) loadNotifs(); setNotifOpen(o => !o); }}
                className={`relative w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${isDark ? 'text-slate-400 hover:bg-white/10' : 'text-slate-500 hover:bg-slate-100'}`}
                aria-label={t('layout.notifications')}
              >
                <svg style={{ width: 18, height: 18 }} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                {unreadCount > 0 && (
                  <span className="absolute top-0.5 right-0.5 min-w-[14px] h-3.5 px-0.5 bg-red-500 text-white text-[8px] rounded-full flex items-center justify-center font-bold leading-none">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {notifOpen && (
                <div className="absolute right-0 mt-1.5 w-80 max-w-[calc(100vw-1rem)] rounded-xl z-[70] overflow-hidden" style={{ background: '#0D1F2D', border: '1px solid #1B3142', boxShadow: '0 16px 48px rgba(0,0,0,0.55)' }}>
                  <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid #1B3142', background: '#07131C' }}>
                    <div>
                      <p className="text-[13px] font-semibold text-slate-100">{t('layout.notifications')}</p>
                      <p className="text-[11px] text-slate-500">{t('layout.unreadCount', { count: unreadCount })}</p>
                    </div>
                    {unreadCount > 0 && (
                      <button onClick={handleMarkAll} className="text-xs text-orange-400 hover:text-orange-300 font-medium">
                        {t('layout.markAllRead')}
                      </button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-10 text-center text-sm text-slate-500">{t('layout.noNotifications')}</div>
                    ) : notifications.map(n => (
                      <button
                        key={n.id}
                        onClick={() => handleNotifClick(n)}
                        className={`w-full text-left px-4 py-3 transition-colors ${n.is_read ? 'hover:bg-white/5' : 'hover:bg-white/5'}`}
                        style={{ borderBottom: '1px solid #1B3142', background: n.is_read ? 'transparent' : 'rgba(249,115,22,0.06)' }}
                      >
                        <div className="flex items-start gap-3">
                          {!n.is_read && <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-orange-500 flex-shrink-0" />}
                          <div className={`min-w-0 ${n.is_read ? 'ml-4' : ''}`}>
                            <p className="text-[13px] font-medium text-slate-100">{n.title}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{n.message}</p>
                            <p className="text-[11px] text-slate-600 mt-1">{fmtNotifTime(n.created_at, t)}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Avatar */}
            <div className="relative flex-shrink-0" ref={accountRef}>
              <button
                type="button"
                onClick={() => setAccountMenuOpen((open) => !open)}
                className={`w-7 h-7 rounded-full ${avatarCls} flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 ring-1 ring-white/10 hover:ring-white/20 transition-colors overflow-hidden`}
                title={user?.email}
              >
                {user?.profile_photo ? (
                  <img src={user.profile_photo} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  initials
                )}
              </button>

              {accountMenuOpen && (
                <div
                  className="absolute right-0 mt-2 w-64 rounded-xl z-[70] overflow-hidden"
                  style={{ background: '#0D1F2D', border: '1px solid #1B3142', boxShadow: '0 16px 48px rgba(0,0,0,0.55)' }}
                >
                  <div className="px-4 py-3" style={{ borderBottom: '1px solid #1B3142', background: '#07131C' }}>
                    <p className="text-[13px] font-semibold text-slate-100 truncate">{displayName}</p>
                    <p className="text-[11px] text-slate-500 truncate">{user?.email}</p>
                  </div>
                  <div className="p-1.5">
                    <button
                      type="button"
                      onClick={() => { setAccountMenuOpen(false); toggleTheme(); }}
                      className="w-full text-left px-3 py-2.5 rounded-lg text-sm text-slate-200 hover:bg-white/5 transition-colors"
                    >
                      {isDark ? t('common.lightMode') : t('common.darkMode')}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setAccountMenuOpen(false); navigate('/account'); }}
                      className="w-full text-left px-3 py-2.5 rounded-lg text-sm text-slate-200 hover:bg-white/5 transition-colors"
                    >
                      {t('common.accountSettings')}
                    </button>
                    <button type="button" onClick={() => { setAccountMenuOpen(false); setAnnouncementHistoryOpen(true); }} className="w-full text-left px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:bg-white/5 transition-colors">
                      {language === 'es' ? 'Anuncios y novedades' : 'Announcements and updates'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setAccountMenuOpen(false); onLogout(); navigate('/login'); }}
                      className="w-full text-left px-3 py-2.5 rounded-lg text-sm text-red-300 hover:bg-red-500/10 transition-colors"
                    >
                      {t('common.signOut')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content — the same element tree in both modes. Swapping wrappers
            when a page asks for noPadding remounted that page, so Timesheets,
            Reports and Schedule loaded all of their data twice on entry. */}
        <div className={noPadding ? 'flex-1 overflow-hidden flex flex-col min-h-0' : 'flex-1 overflow-auto'}>
          <div className={noPadding ? 'flex min-h-0 flex-1 flex-col' : 'forxa-page-content'}>
            {routedContent}
          </div>
        </div>

      </main>
      {user && !isPermissionPreview() && <Announcements key={user.id} userId={user.id} es={language === 'es'} historyOpen={announcementHistoryOpen} onHistoryClose={() => setAnnouncementHistoryOpen(false)} />}
    </div>
  );
}
